import Constants from "expo-constants";
import AsyncStorage from "@react-native-async-storage/async-storage";

export const EMULATOR_INIT_STORAGE_KEY = "@memories/emulator_initialized_run";

let hasInitializedEmulatorInSession = false;

/**
 * Resets the in-memory session flag for testing purposes.
 */
export function resetEmulatorSetupSession(): void {
  hasInitializedEmulatorInSession = false;
}

/**
 * Checks whether the current runtime environment is LOCAL and connected to Firebase emulators.
 *
 * @returns {boolean} True if running locally against Firebase emulators.
 */
export function isLocalEmulatorEnvironment(): boolean {
  const vars = Constants.expoConfig?.extra;
  const env = String(vars?.env || process.env.EXPO_PUBLIC_ENV || "").toUpperCase();
  const isLocal = env === "LOCAL";
  const hasEmulatorConfig = Boolean(
    vars?.FUNCTIONS_EMULATOR_HOST ||
    vars?.AUTH_EMULATOR_HOST ||
    process.env.EXPO_PUBLIC_USE_EMULATOR === "1" ||
    String(vars?.PROJECT_ID || process.env.EXPO_PUBLIC_PROJECT_ID || "").startsWith("demo-")
  );
  return isLocal && hasEmulatorConfig;
}

/**
 * Executes initializeEmulatorFunc on the backend if in LOCAL emulator mode upon the first login.
 * Tracks execution per session and persists a run marker in AsyncStorage.
 *
 * @returns {Promise<boolean>} True if initializeEmulatorFunc ran successfully, false otherwise.
 */
export async function initializeEmulatorOnFirstLogin(): Promise<boolean> {
  if (!isLocalEmulatorEnvironment()) {
    return false;
  }

  if (hasInitializedEmulatorInSession) {
    return false;
  }

  hasInitializedEmulatorInSession = true;

  try {
    const alreadyInit = await AsyncStorage.getItem(EMULATOR_INIT_STORAGE_KEY).catch(() => null);
    if (alreadyInit === "true") {
      return false;
    }
  } catch {
    // Continue if storage check fails
  }

  const vars = Constants.expoConfig?.extra;
  const functionsUrl = vars?.FUNCTIONS_URL || process.env.EXPO_PUBLIC_FUNCTIONS_URL;

  const urlsToTry: string[] = [];
  if (functionsUrl) {
    urlsToTry.push(`${functionsUrl}/initializeEmulatorFunc`);
    urlsToTry.push(`${functionsUrl}/setup/initializeEmulatorFunc`);
    const directUrl = functionsUrl.replace(/\/api\/?$/, "/initializeEmulatorFunc");
    if (!urlsToTry.includes(directUrl)) {
      urlsToTry.push(directUrl);
    }
  }
  urlsToTry.push("http://127.0.0.1:9003/demo-memories/europe-north1/initializeEmulatorFunc");
  urlsToTry.push("http://localhost:9003/demo-memories/europe-north1/initializeEmulatorFunc");

  for (const url of urlsToTry) {
    try {
      console.info(`[EmulatorSetup] Triggering initializeEmulatorFunc via ${url}...`);
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (response.ok) {
        console.info(`[EmulatorSetup] initializeEmulatorFunc executed successfully via ${url}`);
        await AsyncStorage.setItem(EMULATOR_INIT_STORAGE_KEY, "true").catch(() => {});
        return true;
      }
      if (response.status === 404 || response.status === 405) {
        const getRes = await fetch(url, { method: "GET" });
        if (getRes.ok) {
          console.info(`[EmulatorSetup] initializeEmulatorFunc executed successfully via GET ${url}`);
          await AsyncStorage.setItem(EMULATOR_INIT_STORAGE_KEY, "true").catch(() => {});
          return true;
        }
      }
    } catch (err) {
      console.warn(`[EmulatorSetup] Failed to contact ${url}:`, err);
    }
  }

  return false;
}
